import React, { useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { QuickSightService } from '@/services/quicksight.service';
import { createEmbeddingContext } from 'amazon-quicksight-embedding-sdk';
import type { EmbeddingContext, DashboardExperience } from 'amazon-quicksight-embedding-sdk';
import { useDashboard } from '@/hooks/useDashboard';
import quicksightConfig from '@/config/quicksight.config';
import { DASHBOARD_READY_CHECK_INTERVAL, DASHBOARD_READY_TIMEOUT } from '@/config/constant';
import CircularProgress from '@mui/material/CircularProgress';
import Typography from '@mui/material/Typography';

interface QuickSightDashboardProps {
  height?: string;
  className?: string;
}

const QuickSightDashboard: React.FC<QuickSightDashboardProps> = ({
  height = '100%',
  className = ''
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const dashboardRef = useRef<DashboardExperience | null>(null);
  const embeddingContextRef = useRef<EmbeddingContext | null>(null);
  const hasInitializedRef = useRef<boolean>(false);
  const isDashboardReadyRef = useRef<boolean>(false);
  const currentDashboardIdRef = useRef<string | null>(null);
  
  const location = useLocation();
  const navigate = useNavigate();
  const { setDashboard, setIsLoading, setError, isLoading, error } = useDashboard();

  // Cleanly extract route segments using React Router state
  const currentPathId = location.pathname.replace(/^\//, '');
  const targetDashboardId = currentPathId || quicksightConfig.defaultDashboardId;

  // ── HOOK 0: SECURE APP-LEVEL REDIRECT ───────────────────────────────────
  useEffect(() => {
    if (!currentPathId) {
      console.log('[Router] Root path detected. Redirecting safely via React Router state.');
      navigate(`/${quicksightConfig.defaultDashboardId}`, { replace: true });
    }
  }, [currentPathId, navigate]);

  // ── EFFECT 1: Framework Embed (Runs ONCE when we have a valid ID) ───────
  useEffect(() => {
    if (!currentPathId) return;

    let isMounted = true;

    const embedInitialDashboard = async () => {
      if (!containerRef.current) return;

      if (hasInitializedRef.current) {
        console.log('[QuickSight] Initialization block locked. Skipping duplicate backend URL request.');
        return;
      }
      hasInitializedRef.current = true;

      try {
        setIsLoading(true);
        setError(null);

        console.log('[QuickSight] Performing ONE-TIME initial session embed for ID:', currentPathId);
        
        const embedUrl = await QuickSightService.getEmbedUrl(currentPathId);
        if (!isMounted) return;

        embeddingContextRef.current ??= await createEmbeddingContext();

        const dashboard = await embeddingContextRef.current.embedDashboard(
          {
            url: embedUrl,
            container: containerRef.current,
            height: height,
            width: '100%',
            withIframePlaceholder: true
          },
          {
            toolbarOptions: {
              export: false, // enable export button in the toolbar only if required
              undoRedo: false,
              reset: true
            }
          }
        );

        dashboardRef.current = dashboard;
        currentDashboardIdRef.current = currentPathId;
        
        await new Promise<void>((resolve) => {
          const checkReady = setInterval(() => {
            if (dashboard) {
              clearInterval(checkReady);
              isDashboardReadyRef.current = true;
              resolve();
            }
          }, DASHBOARD_READY_CHECK_INTERVAL);
          
          setTimeout(() => {
            clearInterval(checkReady);
            isDashboardReadyRef.current = true;
            resolve();
          }, DASHBOARD_READY_TIMEOUT);
        });
        
        setDashboard(dashboard);
        setIsLoading(false);

      } catch (err) {
        console.error('[QuickSight] Root initialization error:', err);
        hasInitializedRef.current = false; 
        if (isMounted) {
          setError(err instanceof Error ? err.message : 'Failed to initialize session framework');
          setIsLoading(false);
        }
      }
    };

    embedInitialDashboard();

    return () => {
      isMounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPathId, setDashboard, setError, height]); 

  // ── EFFECT 2: Client-side Navigation Switcher ──────────────────────────
  useEffect(() => {
    if (!dashboardRef.current || !currentPathId) return;
    
    if (!isDashboardReadyRef.current) {
      console.log('[QuickSight] Dashboard not ready yet. Skipping navigation.');
      return;
    }
    
    if (currentDashboardIdRef.current === targetDashboardId) {
      console.log('[QuickSight] Already on target dashboard:', targetDashboardId, '. Skipping navigation.');
      return;
    }

    const handleDashboardSwitch = async () => {
      try {
        console.log('[QuickSight] URL path mutated. Soft-switching iframe view from:', currentDashboardIdRef.current, 'to:', targetDashboardId);
        // setIsLoading(true);
        await dashboardRef.current!.navigateToDashboard(targetDashboardId);
        currentDashboardIdRef.current = targetDashboardId;
        // setIsLoading(false);
      } catch (err) {
        console.error('[QuickSight] Navigation soft-switch failed:', err);
        setError(err instanceof Error ? err.message : 'Failed to navigate to dashboard');
        // setIsLoading(false);
      }
    };

    handleDashboardSwitch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [targetDashboardId]);

  // ── ERROR STATE ────────────────────────────────────────────────────────────

  if (error) {
    return (
      <div className="quicksight-error" style={{ padding: '20px', textAlign: 'center' }}>
        <h3>Error Loading Analytics Engine</h3>
        <p>{error}</p>
        <button onClick={() => globalThis.location.reload()}>Retry Connection</button>
      </div>
    );
  }

  return (
    <div className={`quicksight-container ${className}`} style={{ position: 'relative' }}>

      {/* ── LOADING OVERLAY ────────────────────────────────────────────────── */}
      {isLoading && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100vw',
          height: '100vh',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          background: 'rgba(255,255,255,0.7)',
          zIndex: 9999
        }}>
          <CircularProgress />
          <Typography style={{ fontSize: '16px', textAlign: 'center', marginTop: 16 }} variant="h6" gutterBottom>
            Please wait while your dashboard data is loading...
          </Typography>
        </div>
      )}
      <div 
        ref={containerRef} 
        style={{ 
          width: '100%', 
          height: height,
          visibility: isLoading ? 'hidden' : 'visible'
        }}
      />
    </div>
  );
};

export default QuickSightDashboard;
